/**
 * ChatPanelBody — Thread-Ansicht oder Empty-State
 *
 * Lädt initial via REST, hört auf WS-Events, rendert Message-Bubbles.
 * Auto-Scroll nach unten bei neuer Nachricht (es sei denn, der User hat
 * manuell hochgescrollt — dann nicht stören).
 *
 * Empty-State: erste Begrüßung mit Branding (vor erster User-Nachricht).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useChatConversation, type UIMessage } from '../../hooks/useChatConversation';
import { getImageUrl } from '../../utils/imageUtils';
import { ChatMessageBubble } from './ChatMessageBubble';

const FOX_ICON = '/images/branding/rf-fux-yellow.svg';

interface ChatPanelBodyProps {
  enabled: boolean;
  /** Wird vom Eltern-Element bereitgestellt — Eingabefeld nutzt dieselbe Hook-Instanz. */
  conversation: ReturnType<typeof useChatConversation>;
  /** Wird aufgerufen, wenn der User eine Bubble nach links/rechts wischt. */
  onReply?: (message: UIMessage) => void;
}

export function ChatPanelBody({ conversation: state, onReply }: ChatPanelBodyProps): React.ReactElement {
  const { messages, isLoading, error } = state;

  const scrollRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUpRef = useRef(false);
  // Memoriert den senderType der letzten gerenderten Nachricht — wenn der
  // User SELBST eine neue Nachricht hinzufügt (Optimistic), muss garantiert
  // nach unten gescrollt werden, auch wenn er kurz vorher hochgescrollt war
  // (z.B. um zu wischen oder eine alte Nachricht zu zitieren).
  const lastMessageRef = useRef<UIMessage | null>(null);

  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  // Auto-Scroll nach unten bei neuer Nachricht — aber NUR wenn User
  // nicht aktiv hochgescrollt hat (sonst springt die Ansicht weg).
  // EXCEPTION: Wenn die neueste Nachricht vom User SELBST stammt, immer
  // scrollen — er hat sie ja gerade gesendet.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newest = messages[messages.length - 1];
    const wasMine = newest && newest.senderType === 'user';
    const prevLast = lastMessageRef.current;
    const isOwnNew =
      wasMine &&
      (!prevLast || prevLast.id !== newest.id) &&
      (!prevLast || prevLast.clientId !== newest.clientId);
    lastMessageRef.current = newest ?? null;

    if (isUserScrolledUpRef.current && !isOwnNew) return;

    // Doppel-RAF: erst nachdem Layout berechnet wurde
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        // Eigener Send → Flag zurücksetzen, damit nachfolgende Auto-Scrolls
        // wieder nach der normalen Logik laufen.
        if (isOwnNew) isUserScrolledUpRef.current = false;
      });
    });
  }, [messages]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    // Toleranz 80 px — innerhalb dieses Bereichs gilt User als "am Ende"
    isUserScrolledUpRef.current = distanceFromBottom > 80;
  };

  const handleJumpToMessage = useCallback((messageId: string) => {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(messageId)}"]`
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // User HAT manuell weg-gescrollt (zur alten Nachricht). Damit nicht beim
    // nächsten WS-Echo der Auto-Scroll springt, Flag setzen.
    isUserScrolledUpRef.current = true;

    setHighlightedId(messageId);
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedId(null);
      highlightTimerRef.current = null;
    }, 1500);
  }, []);

  // Cleanup beim Unmount
  useEffect(() => () => {
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
    }
  }, []);

  // Loading state: dezent, nicht-störend
  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-[11px] opacity-40 font-mono">Lädt …</p>
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <p className="text-[12px] opacity-60 font-mono">
          Konversation konnte nicht geladen werden. Bitte später erneut versuchen.
        </p>
      </div>
    );
  }

  // Empty-State: keine Nachrichten — Begrüßung + Brand
  if (messages.length === 0) {
    return (
      <div
        data-lenis-prevent
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-styled touch-pan-y"
      >
        <div className="flex flex-col items-center justify-center h-full px-6 py-8 text-center">
          <div className="glass-surface-soft rounded-full w-14 h-14 p-3 ring-2 ring-brand/40 mb-4 flex items-center justify-center">
            <img src={getImageUrl(FOX_ICON)} alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
          </div>
          <h2 className="text-lg font-semibold tracking-tight mb-2 leading-snug">
            Hallo, hier ist RENDITEFUCHS.
          </h2>
          <p className="text-[13px] opacity-70 leading-relaxed max-w-[280px]">
            Schreib uns, was dich beschäftigt — wir antworten so schnell wie möglich.
          </p>
        </div>
      </div>
    );
  }

  // Thread-Ansicht
  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      data-lenis-prevent
      className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-styled touch-pan-y px-3 py-3"
    >
      <div className="flex flex-col gap-2">
        {messages.map((m) => (
          <ChatMessageBubble
            key={m.clientId ?? m.id}
            message={m}
            isHighlighted={highlightedId === m.id}
            onJumpToMessage={handleJumpToMessage}
            {...(onReply ? { onReply: () => onReply(m) } : {})}
          />
        ))}
      </div>
    </div>
  );
}
